import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import {
  type CardholderAccountStatusEnum,
  IssuingProviders,
} from '@webapp/modules/spending-cards'
import { CardStatuses, type CardStatusEnum } from '../enums/CardStatusEnum'
import { type CardTypeEnum } from '../enums/CardTypeEnum'
import { type IssuingProviderEnum } from '../enums/IssuingProvider'

export interface DetailedCardConstructor {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  status: CardStatusEnum
  createdAt: string
  updatedAt: string
  requireActivation: boolean
  balance: string
  currencyName: CurrencyName
  expiryDate?: Nullable<string>
  shippingAddress: Nullable<{
    name: Nullable<string>
    addressLine1: Nullable<string>
    addressLine2: Nullable<string>
    postalCode: Nullable<string>
    city: Nullable<string>
    stateProvince: Nullable<string>
    country: Nullable<string>
  }>
  cardholder: {
    id: number
    externalId: string
    userId: number
    firstName: string
    lastName: string
    email: string
    secondaryCategoryId: number
    profileImage: string
    tosAccepted: boolean
    status: CardholderAccountStatusEnum
    provider: IssuingProviderEnum
    billingAddress: {
      name: string
      addressLine1: string
      addressLine2: Nullable<string>
      postalCode: string
      city: string
      stateProvince: string
      country: string
    }
  }
}

/**
 * `DetailedCard` represents the shape of a spending card as seen by its cardholder.
 */
export class DetailedCard {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  status: CardStatusEnum
  createdAt: string
  updatedAt: string
  requireActivation: boolean
  balance: string
  currencyName: CurrencyName
  expiryDate: string | undefined
  shippingAddress: Nullable<{
    name: Nullable<string>
    addressLine1: Nullable<string>
    addressLine2: Nullable<string>
    postalCode: Nullable<string>
    city: Nullable<string>
    stateProvince: Nullable<string>
    country: Nullable<string>
  }>
  cardholder: {
    id: number
    externalId: string
    userId: number
    firstName: string
    lastName: string
    email: string
    secondaryCategoryId: number
    profileImage: string
    tosAccepted: boolean
    status: CardholderAccountStatusEnum
    provider: IssuingProviderEnum
    billingAddress: {
      name: string
      addressLine1: string
      addressLine2: Nullable<string>
      postalCode: string
      city: string
      stateProvince: string
      country: string
    }
  }

  constructor(data: DetailedCardConstructor) {
    this.id = data.id
    this.externalId = data.externalId
    this.last4Digits = data.last4Digits
    this.type = data.type
    this.status = data.status
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.requireActivation = data.requireActivation
    this.balance = data.balance
    this.currencyName = data.currencyName
    this.expiryDate = data.expiryDate ?? undefined
    this.shippingAddress = data.shippingAddress
    this.cardholder = data.cardholder
  }

  get cardholderName(): string {
    return `${this.cardholder.firstName} ${this.cardholder.lastName}`
  }

  get isActive(): boolean {
    return this.status === CardStatuses.ACTIVE
  }

  get isPendingActivation(): boolean {
    return this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isLocked(): boolean {
    return !this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isCanceled(): boolean {
    return this.status === CardStatuses.CANCELED
  }

  get isAirwallex(): boolean {
    return this.cardholder.provider === IssuingProviders.AIRWALLEX
  }

  get isStripe(): boolean {
    return this.cardholder.provider === IssuingProviders.STRIPE
  }
}
